<script lang="ts">
  import Panel from '@components/panel.svelte'
  import Image from '@components/image.svelte'
  import {
    type FoulType,
    type LegacyFieldGoalEvent,
    type FieldGoalEvent,
    type NbaEvent,
    type GameraTeamReference,
    getTeamIdFromNbaEvent,
    getPlayerIdFromNbaEvent,
  } from '@statmuse/core/gamera'
  import { players } from './stores'
  import { round, some } from 'lodash-es'

  export let play: NbaEvent | undefined
  export let awayTeam: GameraTeamReference | undefined
  export let homeTeam: GameraTeamReference | undefined

  const shotSummary = (play: FieldGoalEvent | LegacyFieldGoalEvent) => {
    if (play.isThreePoint && play.isMade) return 'three'
    if (play.isThreePoint) return '3P miss'
    if (play.type === 'legacyFieldGoal') return 'jumper'
    // layup/putback/jumper/floater/dunk/hook/bank/alley oop
    let shotDescription = ''
    switch (play.shotType) {
      case 'jumpShot':
      case 'jumpShotDriving':
      case 'jumpShotTurnaround':
      case 'jumpShotRunning':
        shotDescription = 'jumper'
        break
      case 'jumpShotBank':
      case 'jumpShotDrivingBank':
      case 'jumpShotTurnaroundBank':
      case 'jumpShotFadeawayBank':
        shotDescription = 'bank'
        break
      case 'jumpShotDrivingFloating':
      case 'jumpShotDrivingFloatingBank':
      case 'jumpShotFloating':
        shotDescription = 'floater'
        break
      case 'jumpShotTurnaroundFadeaway':
      case 'jumpShotTurnaroundFadeawayBank':
      case 'jumpShotFadeaway':
        shotDescription = 'fadeaway'
        break
      case 'jumpShotStepback':
      case 'jumpShotStepbackBank':
        shotDescription = 'stepback'
        break
      case 'jumpShotPullup':
      case 'jumpShotPullupBank':
      case 'jumpShotRunningPullup':
        shotDescription = 'pullup'
        break
      case 'layup':
      case 'layupCutting':
      case 'layupDriving':
      case 'layupReverse':
      case 'layupDrivingReverse':
      case 'layupRunningReverse':
      case 'layupRunning':
        shotDescription = 'layup'
        break
      case 'layupCuttingFingerRoll':
      case 'layupDrivingFingerRoll':
      case 'layupRunningFingerRoll':
      case 'layupFingerRoll':
        shotDescription = 'finger roll'
        break
      case 'layupPutback':
        shotDescription = 'putback'
        break
      case 'layupAlleyOop':
      case 'layupRunningAlleyOop':
      case 'dunkAlleyOop':
      case 'dunkRunningAlleyOop':
        shotDescription = 'alley-oop'
        break
      case 'tipShot':
      case 'layupTip':
        shotDescription = 'tip'
        break
      case 'dunk':
      case 'dunkTip':
      case 'dunkDriving':
      case 'dunkDrivingReverse':
      case 'dunkPutback':
      case 'dunkRunning':
      case 'dunkReverse':
      case 'dunkRunningReverse':
      case 'dunkCutting':
        shotDescription = 'dunk'
        break
      case 'hook':
      case 'hookBank':
      case 'hookDriving':
      case 'hookDrivingBank':
      case 'hookTurnaround':
      case 'hookTurnaroundBank':
        shotDescription = 'hook'
        break
      default:
        shotDescription = '2PT'
    }
    if (!play.isMade) {
      shotDescription += ' miss'
    }
    return shotDescription
  }

  const foulSummary = (foul?: FoulType) => {
    switch (foul) {
      case 'offensive':
        return 'Off foul'
      case 'flagrantOne':
        return 'Flagrant 1'
      case 'flagrantTwo':
        return 'Flagrant 2'
      case 'technical':
        return 'Technical'
      default:
        return 'Foul'
    }
  }

  $: team =
    awayTeam?.teamId === getTeamIdFromNbaEvent(play)
      ? awayTeam
      : homeTeam?.teamId === getTeamIdFromNbaEvent(play)
      ? homeTeam
      : undefined

  $: player = $players[getPlayerIdFromNbaEvent(play)]
</script>

<Panel
  class="!pb-0"
  background={team?.colors?.backgroundColor}
  foreground={team?.colors?.foregroundColor}
>
  <div class="flex justify-between">
    <p class="font-semibold">
      {`${
        play?.period <= 4
          ? play?.period
          : play?.period === 5
          ? ''
          : play?.period - 4
      }${play?.period <= 4 ? 'Q' : 'OT'}`}
      {play?.type === 'endPeriod' ? '0:00' : play?.clock ?? '12:00'}
    </p>
    <p class="flex">
      <span
        class:font-semibold={(play?.type === 'fieldGoal' ||
          play?.type === 'freeThrow') &&
          play.isMade &&
          team?.teamId === awayTeam?.teamId}
      >
        {play?.awayPoints}
      </span>
      <span>-</span>
      <span
        class:font-semibold={(play?.type === 'fieldGoal' ||
          play?.type === 'freeThrow') &&
          play.isMade &&
          team?.teamId === homeTeam?.teamId}
      >
        {play?.homePoints}
      </span>
    </p>
  </div>
  <div class="flex mt-2">
    <div class="flex-1 space-y-1 pb-2 pt-1">
      {#if play?.type === 'lineupChange'}
        <div class="flex items-center gap-3">
          <div
            class="rounded-2xl px-2"
            style={team?.colors
              ? `background:${team.colors.foregroundColor};color:${team.colors.backgroundColor};`
              : undefined}
          >
            Sub
          </div>
          <p>
            {`${play.subbedInPlayerIds
              .map((id) => $players[id]?.shortName ?? $players[id]?.longName)
              .filter((x) => !!x)
              .join(', ')}`}
          </p>
        </div>
        {#if play.subbedOutPlayerIds}
          <div class="text-sm">
            {`Out: ${play.subbedOutPlayerIds
              .map((id) => $players[id]?.shortName ?? $players[id]?.longName)
              .filter((x) => !!x)
              .join(', ')}`}
          </div>
        {/if}
      {:else if play?.type === 'fieldGoal'}
        <div class="flex items-center gap-3">
          <div
            class="rounded-2xl px-2"
            style={team?.colors
              ? `background:${team.colors.foregroundColor};color:${team.colors.backgroundColor};`
              : undefined}
          >
            {`${
              some(['layup', 'dunk', 'finger roll', 'tip', 'alley-oop'], (x) =>
                shotSummary(play)?.includes(x),
              )
                ? ''
                : `${round(play.distanceFeet, 0)}' `
            }${shotSummary(play)}`}
          </div>
          <p>
            {player?.shortName ?? player?.longName ?? ''}
          </p>
        </div>
        {#if play.assistPlayerId}
          {@const assisyBy = $players[play.assistPlayerId]}
          <div class="text-sm">
            Assisted by {assisyBy?.shortName ?? assisyBy?.longName ?? ''}
          </div>
        {/if}
      {:else if play?.type === 'freeThrow'}
        <div class="flex items-center gap-3">
          <div
            class="rounded-2xl px-2"
            style={team?.colors
              ? `background:${team.colors.foregroundColor};color:${team.colors.backgroundColor};`
              : undefined}
          >
            {play.isMade ? 'Free throw' : 'FT miss'}
          </div>
          <p>
            {player?.shortName ?? player?.longName}
          </p>
        </div>
        <div class="text-sm">
          Free throw {`${play.attemptNumber}/${play.totalGranted}`}
        </div>
      {:else if play?.type === 'endPeriod'}
        <div class="flex items-center gap-3">
          <div
            class="rounded-2xl px-2"
            style={team?.colors
              ? `background:${team.colors.foregroundColor};color:${team.colors.backgroundColor};`
              : undefined}
          >
            End of quarter
          </div>
        </div>
      {:else if play?.type === 'foul'}
        <div class="flex items-center gap-3">
          <div
            class="rounded-2xl px-2"
            style={team?.colors
              ? `background:${team.colors.foregroundColor};color:${team.colors.backgroundColor};`
              : undefined}
          >
            {foulSummary(play.foulType)}
          </div>
          <p>
            {player?.shortName ?? player?.longName ?? ''}
          </p>
        </div>
        {#if play.foulDrawnPlayerId}
          {@const foulDrawnBy = $players[play.foulDrawnPlayerId]}
          <div class="text-sm">
            Foul drawn by {foulDrawnBy?.shortName ??
              foulDrawnBy?.longName ??
              ''}
          </div>
        {/if}
      {:else if play?.type === 'openTip' || play?.type === 'jumpBall'}
        <div class="flex items-center gap-3">
          <div
            class="rounded-2xl px-2"
            style={team?.colors
              ? `background:${team.colors.foregroundColor};color:${team.colors.backgroundColor};`
              : undefined}
          >
            {play.type === 'openTip' ? 'Tip off' : 'Jump ball'}
          </div>
          <p>
            {player?.shortName ?? player?.longName ?? ''}
          </p>
        </div>
        {#if play.awayPlayerId && play.homePlayerId}
          {@const awayPlayer = $players[play.awayPlayerId]}
          {@const homePlayer = $players[play.homePlayerId]}
          <div class="text-sm">
            {`${awayPlayer?.shortName ?? awayPlayer?.longName ?? ''} vs ${
              homePlayer?.shortName ?? homePlayer?.longName ?? ''
            }`}
          </div>
        {/if}
      {:else if play?.type === 'rebound'}
        <div class="flex items-center gap-3">
          <div
            class="rounded-2xl px-2"
            style={team?.colors
              ? `background:${team.colors.foregroundColor};color:${team.colors.backgroundColor};`
              : undefined}
          >
            Rebound
          </div>
          <p>
            {player?.shortName ?? player?.longName ?? ''}
          </p>
        </div>
        <div class="text-sm">
          {play.reboundType === 'defensive'
            ? 'Defensive rebound'
            : 'Offensive rebound'}
        </div>
      {:else if play?.type === 'turnover'}
        <div class="flex items-center gap-3">
          <div
            class="rounded-2xl px-2"
            style={team?.colors
              ? `background:${team.colors.foregroundColor};color:${team.colors.backgroundColor};`
              : undefined}
          >
            Turnover
          </div>
          <p>
            {player?.shortName ?? player?.longName ?? ''}
          </p>
        </div>
        {#if play.stealPlayerId}
          {@const stolenBy = $players[play.stealPlayerId]}
          <div class="text-sm">
            Stolen by {stolenBy?.shortName ?? stolenBy?.longName ?? ''}
          </div>
        {/if}
      {:else if play?.type === 'ejection' || play?.type === 'violation'}
        <div class="flex items-center gap-3">
          <div
            class="rounded-2xl px-2"
            style={team?.colors
              ? `background:${team.colors.foregroundColor};color:${team.colors.backgroundColor};`
              : undefined}
          >
            {play.type === 'ejection' ? 'Ejection' : 'Violation'}
          </div>
          <p>
            {player?.shortName ?? player?.longName ?? ''}
          </p>
        </div>
      {:else if play?.type === 'teamTimeout'}
        <div class="flex items-center gap-3">
          <div
            class="rounded-2xl px-2"
            style={team?.colors
              ? `background:${team.colors.foregroundColor};color:${team.colors.backgroundColor};`
              : undefined}
          >
            Timeout
          </div>
          <p>
            {team?.nickname ?? ''}
          </p>
        </div>
      {:else if play?.type === 'challengeTimeout'}
        <div class="flex items-center gap-3">
          <div
            class="rounded-2xl px-2"
            style={team?.colors
              ? `background:${team.colors.foregroundColor};color:${team.colors.backgroundColor};`
              : undefined}
          >
            Challenge
          </div>
          <p>
            {team?.nickname ?? ''}
          </p>
        </div>
      {:else if play?.type === 'review' || play?.type === 'stoppage'}
        <div class="flex items-center gap-3">
          <div
            class="rounded-2xl px-2"
            style={team?.colors
              ? `background:${team.colors.foregroundColor};color:${team.colors.backgroundColor};`
              : undefined}
          >
            {play.type === 'stoppage' ? 'Stoppage' : 'Review'}
          </div>
        </div>
      {/if}
    </div>
    {#if player?.media.bust}
      <Image
        src={player.media.bust.imageUrl}
        alt={player.longName ?? ''}
        width={120}
        height={90}
        class="h-[75px] max-w-[100px] object-contain object-right-bottom self-end"
      />
    {/if}
  </div>
</Panel>
